export const defaultAlerts = () => ({
  payrollUnpaid: false,
  docsMissing: 0,
  reviewDue: false,
  leavePending: false,
});

const defaultLocation = () => ({
  country: '',
  city: '',
  timezone: '',
});

const defaultProbation = () => ({
  inProbation: false,
  endDate: null,
  status: 'not_applicable',
  outcomeDate: '',
});

const defaultPto = () => ({
  balances: {
    annual: { entitled: 0, used: 0, remaining: 0 },
    sick: { entitled: 0, used: 0, remaining: 0 },
    unpaid: { used: 0 },
  },
  requests: {},
  calendarCache: {},
  nextReturnDate: null,
  balanceDays: 0,
});

const defaultCompensation = () => ({
  base: null,
  allowances: [],
  deductions: [],
  paySchedule: null,
  totals: null,
  status: 'pending',
  payslips: {},
});

const defaultPerformance = () => ({
  currentRating: null,
  nextReviewDate: null,
  reviews: {},
  goals: {},
  feedback: {},
});

const defaultQualityOfHire = () => ({
  betaScore: null,
  rampStatus: 'not_started',
  rampDate: '',
  managerRating: null,
  notes: '',
  updatedAt: null,
  updatedBy: null,
});

const defaultDocuments = () => ({
  files: {},
  required: [],
  missingCount: 0,
});

const defaultPortal = () => ({
  invite: {
    status: 'sent', // sent | accepted | expired | revoked
    sentAt: null,
    acceptedAt: null,
    invitedBy: null,
    inviteEmail: null,
  },
  lastLoginAt: null,
  permissions: ['employee'],
  featureFlags: {},
});

const cleanString = (value) => (typeof value === 'string' ? value.trim() : '');

const coalesce = (value, fallback) => (value === undefined || value === null ? fallback : value);

const normalizeLocation = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) return { city: value.trim() };
  return {};
};

export const buildEmployeeRecordFromHire = ({
  employeeId,
  companyUID,
  createdBy,
  createdByEmail,
  hire = {},
  existingEmployee = {},
  status = 'onboarding',
  source = 'onboarding_manual',
}) => {
  const now = Date.now();
  const hireData = hire || {};
  const original = existingEmployee || {};

  const next = {
    ...original,
    contact: { ...(original.contact || {}) },
    meta: { ...(original.meta || {}) },
    profile: { ...(original.profile || {}) },
    lifecycle: { ...(original.lifecycle || {}) },
    pto: { ...(original.pto || {}) },
    compensation: { ...(original.compensation || {}) },
    documents: { ...(original.documents || {}) },
    performance: { ...(original.performance || {}) },
    qualityOfHire: { ...(original.qualityOfHire || {}) },
    portal: { ...(original.portal || {}) },
  };

  // ==== Meta & identification ====
  next.meta = next.meta || {};
  if (!next.meta.createdAt) {
    next.meta.createdAt = now;
  }
  next.meta.updatedAt = now;
  if (createdBy && !next.meta.createdBy) {
    next.meta.createdBy = createdBy;
  }
  if (createdByEmail && !next.meta.createdByEmail) {
    next.meta.createdByEmail = createdByEmail;
  }
  if (companyUID) {
    next.meta.companyUID = companyUID;
  }
  if (employeeId) {
    next.meta.employeeUID = employeeId;
  }
  const employeeNumber = cleanString(
    hireData.employeeNumber || hireData.employeeId || next.meta.employeeNumber || next.employeeId || ''
  );
  if (employeeNumber) {
    next.meta.employeeNumber = employeeNumber;
    next.employeeId = employeeNumber;
  } else if (next.meta.employeeNumber) {
    next.employeeId = next.meta.employeeNumber;
  }
  if (hireData.personUID) {
    next.meta.personUID = hireData.personUID;
  }
  if (hireData.candidateId) {
    next.meta.candidateId = hireData.candidateId;
    next.candidateId = hireData.candidateId;
  }
  if (hireData.applicantId) {
    next.meta.applicantId = hireData.applicantId;
  }
  if (hireData.connectionKey) {
    next.meta.connectionKey = hireData.connectionKey;
  }
  if (hireData.roleId) {
    next.meta.roleId = hireData.roleId;
    next.roleId = hireData.roleId;
  }
  if (hireData.ownerRecruiterId) {
    next.meta.ownerRecruiterId = hireData.ownerRecruiterId;
    next.ownerRecruiterId = hireData.ownerRecruiterId;
  }
  if (hireData.ownerRecruiterEmail) {
    next.meta.ownerRecruiterEmail = hireData.ownerRecruiterEmail;
    next.ownerRecruiterEmail = hireData.ownerRecruiterEmail;
  }
  next.meta.active = coalesce(next.meta.active, status !== 'inactive' && status !== 'offboarded');
  next.meta.source = next.meta.source || source;
  if (hireData.primarySource) {
    next.meta.primarySource = hireData.primarySource;
    next.primarySource = hireData.primarySource;
  }
  if (hireData.sourceCategory) {
    next.meta.sourceCategory = hireData.sourceCategory;
    next.sourceCategory = hireData.sourceCategory;
  }
  if (Array.isArray(hireData.sourceTags)) {
    next.meta.sourceTags = hireData.sourceTags;
    next.sourceTags = hireData.sourceTags;
  }

  // ==== Profile ====
  next.profile = next.profile || {};
  next.profile.location = {
    ...defaultLocation(),
    ...(next.profile.location || {}),
    ...normalizeLocation(hireData.location),
  };
  next.profile.probation = {
    ...defaultProbation(),
    ...(next.profile.probation || {}),
  };

  const name = cleanString(hireData.name || next.profile.name || original.name || '');
  if (name) {
    next.profile.name = name;
    next.name = name;
  }

  const photoUrl = cleanString(hireData.photoUrl || next.profile.photoUrl || '');
  if (photoUrl) {
    next.profile.photoUrl = photoUrl;
  }

  const personalEmail = cleanString(
    hireData.personalEmail || hireData.email || next.profile.email || next.contact.email || ''
  );
  const workEmail = cleanString(hireData.workEmail || next.profile.workEmail || next.contact.workEmail || '');
  const phone = cleanString(hireData.phone || next.profile.phone || next.contact.phone || '');

  next.contact = next.contact || {};
  if (personalEmail) {
    next.profile.email = personalEmail;
    next.contact.email = personalEmail;
    next.contact.personalEmail = personalEmail;
  }
  if (workEmail) {
    next.profile.workEmail = workEmail;
    next.contact.workEmail = workEmail;
  }
  if (phone) {
    next.profile.phone = phone;
    next.contact.phone = phone;
    next.contact.personalPhone = phone;
  }

  if (hireData.roleTitle || hireData.role) {
    const roleTitle = cleanString(hireData.roleTitle || hireData.role || '');
    if (roleTitle) {
      next.profile.roleTitle = roleTitle;
      next.role = roleTitle;
    }
  } else if (next.profile.roleTitle) {
    next.role = next.profile.roleTitle;
  }

  if (hireData.department || next.profile.department) {
    const department = cleanString(hireData.department || next.profile.department || '');
    if (department) {
      next.profile.department = department;
      next.department = department;
    }
  }

  if (hireData.managerUID) {
    next.profile.managerUID = hireData.managerUID;
    next.managerId = hireData.managerUID;
  } else if (hireData.managerEmail) {
    next.profile.managerUID = hireData.managerEmail;
    next.managerId = hireData.managerEmail;
  } else if (next.profile.managerUID) {
    next.managerId = next.profile.managerUID;
  }

  if (hireData.managerName) {
    next.managerName = hireData.managerName;
  }

  const employmentType = cleanString(hireData.employmentType || next.profile.employmentType || 'full_time');
  next.profile.employmentType = employmentType || 'full_time';

  const startDate = cleanString(hireData.startDate || next.profile.startDate || original.startDate || '');
  if (startDate) {
    next.profile.startDate = startDate;
    next.startDate = startDate;
  }

  const endDate = cleanString(hireData.endDate || next.profile.endDate || '');
  if (endDate) {
    next.profile.endDate = endDate;
  }

  if (hireData.probation && typeof hireData.probation === 'object') {
    next.profile.probation = {
      ...next.profile.probation,
      ...hireData.probation,
    };
  }
  if (typeof hireData.inProbation === 'boolean') {
    next.profile.probation.inProbation = hireData.inProbation;
  }
  if (hireData.probationEndDate) {
    next.profile.probation.endDate = hireData.probationEndDate;
  }
  next.probation = {
    active: Boolean(next.profile.probation.inProbation),
    ...next.profile.probation,
  };

  if (hireData.buddyMentor) {
    next.profile.buddyMentor = hireData.buddyMentor;
  }

  // ==== Lifecycle ====
  next.lifecycle = next.lifecycle || {};
  next.lifecycle.status = status || next.lifecycle.status || 'active';
  next.lifecycle.lastActivityAt = now;
  next.documents = {
    ...defaultDocuments(),
    ...(next.documents || {}),
  };
  next.lifecycle.alerts = {
    ...defaultAlerts(),
    ...(next.lifecycle.alerts || {}),
    docsMissing: coalesce(next.documents.missingCount, 0),
  };
  next.status = next.lifecycle.status;

  // ==== PTO ====
  next.pto = {
    ...defaultPto(),
    ...(next.pto || {}),
  };
  if (next.pto.balances?.annual && typeof next.pto.balances.annual.remaining === 'number') {
    next.pto.balanceDays = coalesce(next.pto.balanceDays, next.pto.balances.annual.remaining);
  }

  // ==== Compensation ====
  next.compensation = {
    ...defaultCompensation(),
    ...(next.compensation || {}),
  };

  // ==== Performance ====
  next.performance = {
    ...defaultPerformance(),
    ...(next.performance || {}),
  };

  // ==== Quality of Hire Beta ====
  next.qualityOfHire = {
    ...defaultQualityOfHire(),
    ...(next.qualityOfHire || {}),
  };

  // ==== Documents ====
  next.documents.required = Array.isArray(next.documents.required) ? next.documents.required : [];
  next.documents.files = { ...(next.documents.files || {}) };
  next.documents.missingCount = coalesce(next.documents.missingCount, 0);

  // ==== Portal ====
  next.portal = {
    ...defaultPortal(),
    ...(next.portal || {}),
  };
  if (personalEmail && !next.portal.invite.inviteEmail) {
    next.portal.invite.inviteEmail = personalEmail;
  }
  if (!next.portal.permissions || !next.portal.permissions.length) {
    next.portal.permissions = ['employee'];
  }

  // Keep linkage metadata handy at top-level for legacy code
  if (hireData.applicantId) {
    next.applicantId = hireData.applicantId;
  }
  if (hireData.candidateId) {
    next.candidateId = hireData.candidateId;
  }
  if (hireData.connectionKey) {
    next.connectionKey = hireData.connectionKey;
  }
  if (hireData.roleId) {
    next.roleId = hireData.roleId;
  }
  if (hireData.ownerRecruiterId) {
    next.ownerRecruiterId = hireData.ownerRecruiterId;
  }
  if (hireData.primarySource) {
    next.primarySource = hireData.primarySource;
  }
  if (hireData.sourceCategory) {
    next.sourceCategory = hireData.sourceCategory;
  }
  if (Array.isArray(hireData.sourceTags)) {
    next.sourceTags = hireData.sourceTags;
  }

  // Nationalization facts are protected server-side and must never be copied to
  // the broadly readable employee roster. Also remove fields left by early
  // prototypes whenever an existing employee is rewritten through this helper.
  delete next.nationalization;
  delete next.nationality;
  delete next.citizenship;
  if (next.identity && typeof next.identity === 'object') {
    delete next.identity.nationality;
    delete next.identity.citizenship;
  }
  if (next.regulatory && typeof next.regulatory === 'object') {
    [
      'nationalization',
      'employment',
      'nationality',
      'nationalityVerification',
      'occupation',
      'wage',
      'eligibilityEvidence',
      'extras',
      'establishment_id',
      'establishmentId'
    ].forEach((key) => delete next.regulatory[key]);
    if (Object.keys(next.regulatory).length === 0) delete next.regulatory;
  }

  return next;
};
